<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';

/**
 * Stream of particles flowing out from behind the bridge badge (the zero
 * measurement button) into the detector interface while a zero measurement
 * settles.
 *
 * Belongs inside the pair row's first cell (the interface tile), which it
 * positions itself against; its flight path is derived from the row's
 * geometry (see _pairRowGeometry.scss) and it slots into the row's stacking
 * order between the cards' backgrounds and their content, under the badge.
 *
 * Turning `active` off stops the emission: dots in flight finish their trip,
 * no new ones start, and the layer goes once every dot has landed.
 */
const props = defineProps<{
  active?: boolean;
}>();

// Dots in the stream; their timing and spread live in the styles.
const PARTICLE_COUNT = 12;

// Stopping the stream: once it goes inactive, each dot is parked when its
// current flight completes (animationiteration), and the layer goes once
// every dot is parked. A fallback timer covers browsers that never fire the
// event (e.g. the layer hidden under reduced motion); it is longer than one
// flight plus the largest start delay.
const STOP_FALLBACK_MS = 3000;
const stopping = ref(false);
const parked = ref<boolean[]>(Array<boolean>(PARTICLE_COUNT).fill(false));
const shown = computed(() => props.active || stopping.value);
let stopFallback: number | undefined;

function clearStopFallback() {
  if (stopFallback !== undefined) {
    window.clearTimeout(stopFallback);
    stopFallback = undefined;
  }
}

function finishStopping() {
  stopping.value = false;
  clearStopFallback();
}

function onParticleIteration(index: number) {
  if (!stopping.value) {
    return;
  }
  parked.value[index] = true;
  if (parked.value.every(Boolean)) {
    finishStopping();
  }
}

watch(
  () => props.active,
  (active, wasActive) => {
    clearStopFallback();
    if (active) {
      parked.value = Array<boolean>(PARTICLE_COUNT).fill(false);
      stopping.value = false;
    } else if (wasActive) {
      stopping.value = true;
      stopFallback = window.setTimeout(finishStopping, STOP_FALLBACK_MS);
    }
  },
);

onBeforeUnmount(clearStopFallback);
</script>

<template>
  <div v-if="shown" class="tile-particles" aria-hidden="true">
    <span
      v-for="n in PARTICLE_COUNT"
      :key="n"
      class="tile-particle"
      :class="{ 'tile-particle--parked': parked[n - 1] }"
      @animationiteration="onParticleIteration(n - 1)"
    />
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:list';
@use 'sass:math';
@use './pairRowGeometry' as geometry;

// Dots start under the bridge badge at the center of the gap and fly into
// the interface tile, under the center of its image.
$particle-entry: math.div(geometry.$tile-column-gap, 2);
$particle-size: 12px;
$particle-count: 12;
// The flight is a sixth shorter than the former one from the array image
// center (the tile's width less its image center, plus half the gap); the
// duration and count keep that stream's pace and spacing.
$particle-duration: 1.2s;
// Funnel: dots leave the badge spread over most of its height while grown
// (the mouth; the button is 68px grown by 1.35, so its radius is 46px) and
// converge to within a few pixels at the interface image center (the neck).
$particle-mouth: 40px;
$particle-neck: 4px;
// Faint tint the particles are painted in.
$particle-tint: 30%;

// The layer covers the interface tile and half the gap, up to the badge's
// center; like the bridge, it paints from the first cell under the cards'
// content (image, text) but over their backgrounds and the bridge (z-index 3
// in the row's stacking order, see RTSystemPairRow), and under the badge.
// Dots start hidden under the badge on its center line, spread over most of
// its height ($particle-mouth), emerge all along its left rim and fly in
// straight lines to the center of the interface image ($particle-neck),
// where they disappear under it: a funnel, wide at the badge, narrow at the
// interface. They are soft glows in a faint tint. The row's drop-shadow
// filter also shadows each dot.
.tile-particles {
  // The particles' color: a faint tint of the progress bar's blue over the surface.
  --particle-color: color-mix(
    in srgb,
    rgb(var(--v-theme-info)) #{$particle-tint},
    rgb(var(--v-theme-surface))
  );
  position: absolute;
  inset: 0 (-$particle-entry) 0 0;
  z-index: 3;
  pointer-events: none;
}

.tile-particle {
  position: absolute;
  // Centered on the source point (the layer's right edge).
  left: calc(100% - #{math.div($particle-size, 2)});
  top: var(--particle-y-start);
  width: $particle-size;
  height: $particle-size;
  border-radius: 50%;
  // Soft glow: solid core, rim fading to transparent, all in the fill's color.
  background: radial-gradient(circle, var(--particle-color) 40%, transparent 70%);
  opacity: 0;
  animation: tile-particle-flow $particle-duration linear infinite;

  // Evenly spaced delays keep the stream continuous. Per dot: the vertical
  // offset it starts at (across the mouth) and the one it ends at (within the
  // neck). Even dots are a touch smaller for depth.
  $start-offsets: 0px -32px 24px (-$particle-mouth) 12px $particle-mouth -16px 36px -8px -26px
    30px -36px;
  $end-offsets: 0px 3px -2px (-$particle-neck) 1px $particle-neck -3px 2px -1px 3px -2px 1px;
  @for $i from 1 through $particle-count {
    &:nth-child(#{$i}) {
      --particle-y-start: calc(
        50% + #{list.nth($start-offsets, $i)} - #{math.div($particle-size, 2)}
      );
      --particle-y-end: calc(50% + #{list.nth($end-offsets, $i)} - #{math.div($particle-size, 2)});
      animation-delay: math.div($particle-duration, $particle-count) * ($i - 1);
    }
  }

  &:nth-child(even) {
    scale: 0.75;
  }

  // Parked after its last flight while the stream is stopping.
  &--parked {
    animation: none;
    opacity: 0;
  }
}

// One straight segment: source point → the interface image's center.
@keyframes tile-particle-flow {
  0% {
    left: calc(100% - #{math.div($particle-size, 2)});
    top: var(--particle-y-start);
    opacity: 0;
  }
  8% {
    opacity: 1;
  }
  85% {
    opacity: 1;
  }
  100% {
    left: calc(#{geometry.$tile-image-center} - #{math.div($particle-size, 2)});
    top: var(--particle-y-end);
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tile-particles {
    display: none;
  }
}
</style>
